import React, { useState, useEffect, useCallback } from 'react';
import { SearchIcon, ArrowDown, PlusIcon, ThreeDotsIcon, FilterIcon, CloseIcon } from '../../../public/icons';
import Pagination from '../../shared/Pagination';
import AddAdminModal from './modal/AddAdminModal';
import adminService from '../../../services/admin/admin-authService';
import { useToast } from '../../../context/ToastContext';
import ConfirmDialog from '../../shared/ConfirmDialog';

// Mobile Filter Modal
const MobileFiltersModal = ({ isOpen, onClose, searchQuery, setSearchQuery, statusFilter, setStatusFilter, statusFilters }) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-medium text-[#1C2A46]">Filters</h3>
          <button onClick={onClose} className="p-1">
            <CloseIcon size={20} />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={16} className="text-[#6B635D]" />
              </div>
              <input
                type="text"
                placeholder="Search admins..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2.5 pl-10 pr-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#D4A574] text-sm"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="grid grid-cols-2 gap-2">
              {statusFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => {
                    setStatusFilter(filter.id);
                    onClose();
                  }}
                  className={`px-3 py-2 text-sm rounded border transition-colors ${
                    statusFilter === filter.id
                      ? 'bg-[#D2A146] text-white border-[#D2A146]'
                      : 'bg-white text-[#1C2A46] border-[#DBDBDB] hover:border-[#D2A146]'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button 
            onClick={() => {
              setSearchQuery('');
              setStatusFilter('all');
              onClose();
            }}
            className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded hover:bg-gray-50 transition-colors"
          >
            Clear All
          </button>
          <button 
            onClick={onClose}
            className="flex-1 py-3 bg-[#D2A146] text-white font-medium rounded hover:bg-[#C49660] transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
};

// Mobile Admin Card Component
const MobileAdminCard = ({ admin, onActionClick, showActionsMenu, onStatusChange, onDelete, currentUser }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 flex-shrink-0 bg-gray-100 rounded-full flex items-center justify-center">
            <span className="text-gray-600 font-medium text-base">
              {admin.name?.charAt(0).toUpperCase() || 'A'}
            </span>
          </div>
          <div>
            <div className="font-medium text-gray-900 text-base">{admin.name}</div>
            {admin.isDemo && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                Demo
              </span>
            )}
          </div>
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
          >
            <ThreeDotsIcon className="w-5 h-5" />
          </button>
          
          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg z-20 border border-gray-200">
                <div className="py-1">
                  {admin.status !== 'Active' && (
                    <button
                      onClick={() => {
                        onStatusChange(admin._id, 'Active');
                        setShowMenu(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Activate
                    </button>
                  )}
                  {admin.status !== 'Suspended' && !admin.isDemo && admin.status !== 'Pending' && (
                    <button
                      onClick={() => {
                        onStatusChange(admin._id, 'Suspended');
                        setShowMenu(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Disable
                    </button>
                  )}
                  {currentUser?.role === 'Super Admin' && !admin.isDemo && (
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        onDelete(admin);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      Deactivate Admin
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      <div className="mt-3 space-y-2 pt-3 border-t border-gray-100">
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">Email:</span>
          <span className="text-sm text-gray-700 break-all text-right">{admin.email}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">Role:</span>
          <span className="text-sm text-gray-700">{admin.role}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">Status:</span>
          <div>
            {(() => {
              const statusConfig = {
                'Active': 'bg-[#E7F4ED] text-[#2F8F5B]',
                'Pending': 'bg-[#FFF4D6] text-[#D8A100]',
                'Suspended': 'bg-[#FEE2E2] text-[#DC2626]'
              };
              const colorClass = statusConfig[admin.status] || statusConfig['Pending'];
              return (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full mr-1 ${
                    admin.status === 'Active' ? 'bg-[#2F8F5B]' : 
                    admin.status === 'Pending' ? 'bg-[#D8A100]' : 'bg-[#DC2626]'
                  }`} />
                  {admin.status}
                </span>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminUsersTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isAddAdminModalOpen, setIsAddAdminModalOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const { success, error: showError } = useToast();
  const currentUser = adminService.getCurrentUser();

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminService.getAdmins();
      
      if (response.success) {
        const transformedAdmins = response.data.map(admin => ({
          _id: admin._id,
          id: admin._id,
          name: admin.name,
          email: admin.email,
          role: admin.role,
          status: admin.status,
          phoneNumber: admin.phoneNumber,
          createdAt: admin.createdAt,
          isDemo: admin.isDemo || false
        }));
        
        setAdmins(transformedAdmins);
      } else {
        setError(response.message || 'Failed to fetch admins');
        setAdmins([]);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      setError('Unable to load admins. Please check your connection.');
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const statusFilters = [
    { id: 'all', label: 'All status' },
    { id: 'Active', label: 'Active' },
    { id: 'Pending', label: 'Pending' },
    { id: 'Suspended', label: 'Suspended' }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Active': {
        textColor: 'text-[#2F8F5B]',
        bgColor: 'bg-[#E7F4ED]',
        dotColor: 'bg-[#2F8F5B]',
        label: 'Active'
      },
      'Pending': {
        textColor: 'text-[#D8A100]',
        bgColor: 'bg-[#FFF4D6]',
        dotColor: 'bg-[#D8A100]',
        label: 'Pending'
      },
      'Suspended': {
        textColor: 'text-[#DC2626]',
        bgColor: 'bg-[#FEE2E2]',
        dotColor: 'bg-[#DC2626]',
        label: 'Suspended'
      }
    };

    const config = statusConfig[status] || statusConfig['Pending'];

    return (
      <span className={`hidden md:inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-semibold rounded-full ${config.bgColor} w-fit whitespace-nowrap`}>
        <span className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full ${config.dotColor}`} />
        <span className={config.textColor}>{config.label}</span>
      </span>
    );
  };

  const filteredAdmins = admins.filter(admin => {
    const matchesSearch = 
      admin.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      admin.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      admin.role?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || admin.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAdmins = filteredAdmins.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAdmins.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleAdminAdded = (newAdmin) => {
    fetchAdmins();
    success(`Admin invitation sent successfully to ${newAdmin.email}`);
  };

  const handleActionClick = (adminId, e) => {
    e.stopPropagation();
    setShowActionsMenu(showActionsMenu === adminId ? null : adminId);
    setSelectedAdmin(admins.find(admin => admin._id === adminId));
  };

  const closeActionsMenu = () => {
    setShowActionsMenu(null);
    setSelectedAdmin(null);
  };

  const handleStatusChange = async (adminId, status) => {
    try {
      const response = await adminService.updateAdminStatus(adminId, status);
      if (response.success) {
        const admin = admins.find(a => a._id === adminId);
        success(`${admin?.name}'s status updated to ${status}`);
        fetchAdmins();
      } else {
        showError(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showError('Failed to update status. Please try again.');
    }
  };

  const showDeleteConfirmation = (admin) => {
    if (currentUser && admin._id === currentUser._id) {
      showError('You cannot delete your own account');
      return;
    }

    setSelectedAdmin(admin);
    setConfirmAction({
      title: 'Delete Admin',
      message: `Are you sure you want to delete ${admin.name}? This action cannot be undone.`,
      confirmText: 'Delete Admin',
      confirmVariant: 'danger',
      onConfirm: handleDeleteAdmin
    });
    setShowConfirmDialog(true);
  };

  const handleDeleteAdmin = async () => {
    if (!selectedAdmin) return;

    try {
      const response = await adminService.deleteAdmin(selectedAdmin._id);
      if (response.success) {
        success(`${selectedAdmin.name} has been deleted successfully`);
        fetchAdmins();
      } else {
        showError(response.message || 'Failed to delete admin');
      }
    } catch (error) {
      console.error('Error deleting admin:', error);
      showError('Failed to delete admin. Please try again.');
    } finally {
      setShowConfirmDialog(false);
      setConfirmAction(null);
      setSelectedAdmin(null);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionsMenu && !event.target.closest('.actions-menu')) {
        closeActionsMenu();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showActionsMenu]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-[#D2A146] mx-auto"></div>
          <p className="mt-4 text-gray-600 text-sm">Loading admins...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="p-4 sm:p-6">
          {/* Desktop view */}
          <div className="hidden md:flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Admin Management</h2>
            </div>
            
            <div className="flex flex-row gap-3 items-center">
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <SearchIcon size={18} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search admins..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-9 pr-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent w-56 sm:w-64 text-sm rounded"
                />
              </div>

              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="appearance-none pl-4 pr-8 py-2 focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent bg-white border border-gray-300 text-sm rounded"
                >
                  {statusFilters.map(filter => (
                    <option key={filter.id} value={filter.id}>
                      {filter.label}
                    </option>
                  ))}
                </select>
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <ArrowDown size={14} className="text-gray-400" />
                </div>
              </div>

              <button 
                onClick={() => setIsAddAdminModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#D2A146] text-white font-medium hover:bg-[#C49660] transition-colors whitespace-nowrap text-sm rounded"
              >
                <PlusIcon className="w-4 h-4" color='#fff' />
                Add Admin
              </button>
            </div>
          </div>

          {/* Mobile view header */}
          <div className="md:hidden">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-900">Admin Management</h2>
              <button 
                onClick={() => setMobileFiltersOpen(true)}
                className="flex items-center gap-2 px-3 py-2 border border-[#DBDBDB] rounded text-sm"
              >
                <FilterIcon size={14} />
                Filters
              </button>
            </div>
            
            {/* Mobile Search */}
            <div className="relative mb-3">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search admins..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full p-2.5 pl-10 pr-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent rounded text-sm"
              />
            </div>

            {/* Mobile Add Button */}
            <button 
              onClick={() => setIsAddAdminModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#D2A146] text-white font-medium hover:bg-[#C49660] transition-colors rounded text-sm"
            >
              <PlusIcon className="w-4 h-4" color='#fff' />
              Add Admin
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 px-4 sm:px-6">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error loading admins</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                  <button onClick={fetchAdmins} className="mt-2 text-sm font-medium text-red-800 hover:text-red-900">
                    Try again →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Desktop Table View - Hidden on mobile */}
        <div className="hidden md:block overflow-x-auto border-t border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Admin
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentAdmins.length > 0 ? (
                currentAdmins.map((admin) => (
                  <tr key={admin._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4">
                      <div className="flex items-center">
                        <div className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-gray-600 font-medium text-xs sm:text-sm">
                            {admin.name?.charAt(0).toUpperCase() || 'A'}
                          </span>
                        </div>
                        <div className="ml-2 sm:ml-3">
                          <div className="text-xs sm:text-sm font-medium text-gray-900">{admin.name}</div>
                          {admin.isDemo && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800">
                              Demo
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-900">{admin.email}</div>
                    </td>
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className="text-xs sm:text-sm text-gray-900">{admin.role}</span>
                    </td>
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                      {getStatusBadge(admin.status)}
                    </td>
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap relative">
                      <div className="actions-menu inline-block">
                        <button
                          onClick={(e) => handleActionClick(admin._id, e)}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                          title="Actions"
                        >
                          <ThreeDotsIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        
                        {showActionsMenu === admin._id && (
                          <div className="absolute right-0 mt-2 w-40 sm:w-48 bg-white rounded-lg shadow-lg z-50 border border-gray-200">
                            <div className="py-1">
                              {admin.status !== 'Active' && (
                                <button
                                  onClick={() => handleStatusChange(admin._id, 'Active')}
                                  className="flex items-center w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Activate
                                </button>
                              )}
                              {admin.status !== 'Suspended' && !admin.isDemo && admin.status !== 'Pending' && (
                                <button
                                  onClick={() => handleStatusChange(admin._id, 'Suspended')}
                                  className="flex items-center w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Disable
                                </button>
                              )}
                              {currentUser?.role === 'Super Admin' && !admin.isDemo && (
                                <button
                                  onClick={() => showDeleteConfirmation(admin)}
                                  className="flex items-center w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50"
                                >
                                  Deactivate Admin
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-4 sm:px-6 py-10 sm:py-12 text-center">
                    <div className="text-gray-500">
                      <svg className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                      <p className="mt-2 text-xs sm:text-sm">
                        {searchQuery || statusFilter !== 'all' 
                          ? 'Try adjusting your search or filter'
                          : 'Get started by adding your first admin'}
                      </p>
                      {!searchQuery && statusFilter === 'all' && (
                        <button
                          onClick={() => setIsAddAdminModalOpen(true)}
                          className="mt-3 sm:mt-4 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#D2A146] text-white font-medium hover:bg-[#C49660] transition-colors rounded text-xs sm:text-sm"
                        >
                          Add Admin
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View - Visible only on mobile */}
        <div className="md:hidden px-4 pb-4">
          {currentAdmins.length > 0 ? (
            currentAdmins.map((admin) => (
              <MobileAdminCard
                key={admin._id}
                admin={admin}
                onStatusChange={handleStatusChange}
                onDelete={showDeleteConfirmation}
                currentUser={currentUser}
              />
            ))
          ) : (
            <div className="text-center py-10">
              <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <p className="mt-2 text-sm text-gray-500">
                {searchQuery || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filter'
                  : 'No admins found'}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredAdmins.length > itemsPerPage && (
          <div className="mt-4 px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-t border-gray-200">
            <div className="flex justify-center">
              <Pagination 
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                totalItems={filteredAdmins.length}
                itemsPerPage={itemsPerPage}
              />
            </div>
          </div>
        )}
      </div>

      {/* Add Admin Modal */}
      <AddAdminModal 
        isOpen={isAddAdminModalOpen}
        onClose={() => setIsAddAdminModalOpen(false)}
        onAdminAdded={handleAdminAdded}
      />

      {/* Confirm Dialog */}
      {showConfirmDialog && confirmAction && (
        <ConfirmDialog
          title={confirmAction.title}
          message={confirmAction.message}
          confirmText={confirmAction.confirmText}
          cancelText="Cancel"
          variant={confirmAction.confirmVariant}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => {
            setShowConfirmDialog(false);
            setConfirmAction(null);
          }}
          isOpen={showConfirmDialog}
        />
      )}

      {/* Mobile Filters Modal */}
      <MobileFiltersModal 
        isOpen={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        statusFilters={statusFilters}
      />
    </div>
  );
};

export default AdminUsersTab;